import type { ReactNode } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { CAST, Mention, Portrait, useEmployeesT } from './kit';

/** Hire, brief, approve: the three steps from sign-up to finished work. */
export default function EmployeesHowItWorks() {
  const { t } = useEmployeesT();
  const { ref, state } = useReveal<HTMLElement>(0.2);

  const steps: { title: string; body: string; visual: ReactNode }[] = [
    {
      title: t('Tell us what you do'),
      body: t(
        'Pick the kinds of work you do. We propose a team led by a Chief of Staff, and you hire it in one click.',
      ),
      visual: <PickVisual />,
    },
    {
      title: t('Brief them like a team'),
      body: t(
        'Write to everyone in a group chat or to one person directly. The lead hands each part to the right teammate, and they pass work to each other.',
      ),
      visual: <BriefVisual />,
    },
    {
      title: t('Review and approve'),
      body: t(
        'Finished work comes back for your review. Anything that would go out, like an email or a calendar invite, waits for your OK.',
      ),
      visual: <ReviewVisual />,
    },
  ];

  return (
    <section
      id='how-it-works'
      ref={ref}
      data-reveal={state}
      className={clsxm(styles.reveal, 'scroll-mt-8 py-20 sm:py-28')}
    >
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Your team is ready in a minute.')}
          tail={t('Then it gets to work.')}
        />
        <ol className='mt-14 grid gap-5 lg:grid-cols-3'>
          {steps.map((step, index) => (
            <li
              key={step.title}
              className={clsxm(
                styles.rv,
                'flex flex-col rounded-[28px] p-2',
                tone.surface,
                tone.line,
              )}
              style={delay(index * 120)}
            >
              <div className='flex h-52 items-center justify-center overflow-hidden rounded-[22px] bg-[#f3f3f8] px-5 dark:bg-white/[0.04]'>
                {step.visual}
              </div>
              <div className='px-4 pb-5 pt-6'>
                <p
                  className='text-[13px] font-semibold tabular-nums text-primary-700 dark:text-primary-300'
                  dir='ltr'
                >
                  {String(index + 1).padStart(2, '0')}
                </p>
                <h3
                  className={clsxm(
                    'mt-2 text-xl font-semibold tracking-[-0.015em]',
                    tone.ink,
                  )}
                >
                  {step.title}
                </h3>
                <p className={clsxm('mt-2.5 text-[15px] leading-6', tone.body)}>
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function PickVisual() {
  const { t } = useEmployeesT();
  const roles = [
    { label: t('Founder / CEO'), picked: true },
    { label: t('Marketing & growth'), picked: true },
    { label: t('Sales'), picked: false },
    { label: t('Customer support'), picked: false },
  ];
  return (
    <div aria-hidden='true' className='w-full max-w-[280px]'>
      <div className='flex flex-wrap gap-1.5'>
        {roles.map(({ label, picked }) => (
          <span
            key={label}
            className={clsxm(
              'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
              picked
                ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                : 'bg-white text-[#56566d] ring-1 ring-[#0f0f28]/10 dark:bg-white/[0.06] dark:text-[#a4a4bd] dark:ring-white/10',
            )}
          >
            {picked ? <CheckGlyph className='h-3 w-3' /> : null}
            {label}
          </span>
        ))}
      </div>
      <div className='mt-4 flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_14px_30px_-20px_rgba(22,12,90,0.5)] ring-1 ring-[#0f0f28]/[0.06] dark:bg-[#1b1a27] dark:ring-white/10'>
        <span className='flex -space-x-2 rtl:space-x-reverse'>
          {[CAST.atlas, CAST.nova, CAST.leo, CAST.mira, CAST.ella].map(
            (member) => (
              <Portrait
                key={member.name}
                src={member.photo}
                size={28}
                className='rounded-full ring-2 ring-white dark:ring-[#1b1a27]'
              />
            ),
          )}
        </span>
        <span className='min-w-0'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            {t('Your team of 5')}
          </span>
          <span className={clsxm('block truncate text-xs', tone.quiet)}>
            {t('Led by {{name}}', { name: CAST.atlas.name })}
          </span>
        </span>
      </div>
    </div>
  );
}

function BriefVisual() {
  const { t } = useEmployeesT();
  return (
    <div
      aria-hidden='true'
      className='w-full max-w-[290px] space-y-2.5 rounded-2xl bg-[#121219] p-3.5 text-[13px] text-white/85 shadow-[0_20px_40px_-24px_rgba(16,10,70,0.8)] ring-1 ring-white/10'
    >
      <p className='flex gap-2.5'>
        <span className='flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#e7e5ff] text-[11px] font-semibold text-primary-700'>
          {t('You').slice(0, 1)}
        </span>
        <span>
          <Mention>{CAST.atlas.name}</Mention> {t('plan the Bloom launch')}
        </span>
      </p>
      <p className='flex gap-2.5'>
        <Portrait src={CAST.atlas.photo} size={24} />
        <span>
          <Mention>{CAST.nova.name}</Mention> {t('numbers')} ·{' '}
          <Mention>{CAST.mira.name}</Mention> {t('banner')}
        </span>
      </p>
      <p className='flex items-center gap-2 ps-8 text-[11px] text-white/45'>
        <span className='flex gap-0.5'>
          <span className='h-1 w-1 animate-pulse rounded-full bg-white/60' />
          <span className='h-1 w-1 animate-pulse rounded-full bg-white/60 [animation-delay:150ms]' />
          <span className='h-1 w-1 animate-pulse rounded-full bg-white/60 [animation-delay:300ms]' />
        </span>
        {t('{{a}} and {{b}} are working', {
          a: CAST.nova.name,
          b: CAST.mira.name,
        })}
      </p>
    </div>
  );
}

function ReviewVisual() {
  const { t } = useEmployeesT();
  return (
    <div
      aria-hidden='true'
      className='w-full max-w-[290px] rounded-2xl bg-white p-3.5 shadow-[0_14px_30px_-20px_rgba(22,12,90,0.5)] ring-1 ring-[#0f0f28]/[0.06] dark:bg-[#1b1a27] dark:ring-white/10'
    >
      <p className='flex items-center gap-2.5'>
        <Portrait src={CAST.mira.photo} size={28} />
        <span className='min-w-0 flex-1'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            {t('Spring launch banner')}
          </span>
          <span className={clsxm('block text-xs', tone.quiet)}>
            {t('{{name}} delivered it', { name: CAST.mira.name })}
          </span>
        </span>
        <span className='rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'>
          {t('In review')}
        </span>
      </p>
      <div className='mt-3.5 grid grid-cols-2 gap-2 text-xs font-semibold'>
        <span
          className={clsxm(
            'rounded-full py-2 text-center ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/15',
            tone.ink,
          )}
        >
          {t('Ask for changes')}
        </span>
        <span className='flex items-center justify-center gap-1.5 rounded-full bg-[#10101c] py-2 text-white dark:bg-white dark:text-[#10101c]'>
          <CheckGlyph className='h-3 w-3' />
          {t('Mark done')}
        </span>
      </div>
    </div>
  );
}
